import React, { useMemo } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import useEnvironmentCameraSupport from '../../../shared/hooks/useEnvironmentCameraSupport';
import './ComplaintPhotoUpload.css';

const MAX_PHOTOS = 5;
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const ACCEPT_ATTR = 'image/png,image/jpeg,image/jpg,image/gif,image/webp';

/**
 * Multi-photo picker for complaint create (client-side only until submit).
 * photos: Array<{ id: string, file: File, previewUrl: string }>
 *
 * preferCamera: guest/QR mobile flow — primary action opens the device camera
 * (rear/environment when supported), with gallery as secondary.
 */
function ComplaintPhotoUpload({ photos, onChange, disabled, preferCamera = false }) {
  const remaining = MAX_PHOTOS - (photos?.length || 0);
  const {
    checking: cameraChecking,
    available: rearCameraAvailable,
    reason: cameraUnavailableReason,
  } = useEnvironmentCameraSupport(true);
  const takePhotoEnabled = rearCameraAvailable && !cameraChecking && !disabled;
  const takePhotoTitle = cameraChecking
    ? 'Checking camera…'
    : (cameraUnavailableReason || 'No rear camera available on this device');

  const errorHint = useMemo(() => {
    if ((photos?.length || 0) >= MAX_PHOTOS) {
      return `Maximum of ${MAX_PHOTOS} photos reached.`;
    }
    return null;
  }, [photos]);

  const handleSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const next = [...(photos || [])];
    const errors = [];

    for (const file of files) {
      if (next.length >= MAX_PHOTOS) {
        errors.push(`Only ${MAX_PHOTOS} photos are allowed.`);
        break;
      }
      if (!ACCEPTED_TYPES.includes(file.type) && !/\.(png|jpe?g|gif|webp)$/i.test(file.name)) {
        errors.push(`${file.name}: must be an image (png, jpg, gif, webp).`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        errors.push(`${file.name}: must be 5MB or smaller.`);
        continue;
      }
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    if (errors.length) {
      onChange(next, errors.join(' '));
    } else {
      onChange(next, null);
    }
  };

  const handleRemove = (id) => {
    const removed = (photos || []).find((p) => p.id === id);
    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
    }
    onChange((photos || []).filter((p) => p.id !== id), null);
  };

  const takePhotoClass = preferCamera
    ? 'complaint-photo-add-btn complaint-photo-add-btn--primary'
    : 'complaint-photo-add-btn complaint-photo-add-btn--secondary';

  const takePhotoControl = takePhotoEnabled ? (
    <label className={takePhotoClass}>
      <input
        type="file"
        accept={ACCEPT_ATTR}
        capture="environment"
        onChange={handleSelect}
      />
      <span aria-hidden="true">📷</span>
      Take photo
    </label>
  ) : (
    <button
      type="button"
      className={`${takePhotoClass} disabled`}
      disabled
      title={disabled ? undefined : takePhotoTitle}
      aria-label={disabled ? 'Take photo' : takePhotoTitle}
    >
      <span aria-hidden="true">📷</span>
      Take photo
    </button>
  );

  return (
    <div className="complaint-photo-upload">
      <div className="complaint-photo-upload-header">
        <label className="complaint-photo-upload-label">
          Photos (optional)
        </label>
        <span className="complaint-photo-upload-meta">
          Up to {MAX_PHOTOS} images, 5MB each
        </span>
      </div>
      <p className="complaint-photo-upload-help">
        {preferCamera
          ? 'Take a live photo of the issue at the device, or choose images from your gallery.'
          : 'Attach photos of the issue to help technicians diagnose before inspection.'}
      </p>

      {remaining > 0 && (
        <div className="complaint-photo-actions">
          {preferCamera ? (
            <>
              {takePhotoControl}
              <label className={`complaint-photo-add-btn complaint-photo-add-btn--secondary ${disabled ? 'disabled' : ''}`}>
                <input
                  type="file"
                  accept={ACCEPT_ATTR}
                  multiple
                  disabled={disabled}
                  onChange={handleSelect}
                />
                <span aria-hidden="true">🖼️</span>
                Choose from gallery
              </label>
            </>
          ) : (
            <>
              <label className={`complaint-photo-add-btn complaint-photo-add-btn--primary ${disabled ? 'disabled' : ''}`}>
                <input
                  type="file"
                  accept={ACCEPT_ATTR}
                  multiple
                  disabled={disabled}
                  onChange={handleSelect}
                />
                <span aria-hidden="true">🖼️</span>
                Add photos
              </label>
              {takePhotoControl}
            </>
          )}
        </div>
      )}

      {errorHint && <p className="complaint-photo-upload-limit">{errorHint}</p>}

      {(photos?.length > 0) && (
        <ul className="complaint-photo-preview-list">
          {photos.map((photo) => (
            <li key={photo.id} className="complaint-photo-preview-item">
              <img src={photo.previewUrl} alt={photo.file.name} />
              <button
                type="button"
                className="complaint-photo-remove"
                onClick={() => handleRemove(photo.id)}
                disabled={disabled}
                aria-label={`Remove ${photo.file.name}`}
              >
                ×
              </button>
              <span className="complaint-photo-name" title={photo.file.name}>
                {photo.file.name}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export async function uploadComplaintPhotos(complaintId, photos, { token, photoUploadToken } = {}) {
  if (!photos?.length) return { uploaded: 0, failed: [], succeeded: [] };

  const failed = [];
  const succeeded = [];

  for (const photo of photos) {
    const formData = new FormData();
    formData.append('file', photo.file);
    formData.append('document_type', 'complaint_photo');
    // Prefer JWT when present; photo token covers guest / Registered-via-guest raise.
    // Do not set Content-Type — axios must attach the multipart boundary.
    if (photoUploadToken) {
      formData.append('photo_upload_token', photoUploadToken);
    }

    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      await axios.post(API_ENDPOINTS.COMPLAINTS.DOCUMENTS(complaintId), formData, { headers });
      succeeded.push(photo);
    } catch (err) {
      failed.push({
        name: photo.file.name,
        error: err.response?.data?.error || 'Upload failed',
      });
    }
  }

  return { uploaded: succeeded.length, failed, succeeded };
}

/** Small JPEG data-URL thumbnails for confirmation screens (survives page navigation). */
export async function buildConfirmationPhotoThumbnails(photos, maxEdge = 96) {
  const list = photos || [];
  const thumbs = [];
  for (const photo of list) {
    try {
      const previewUrl = photo.previewUrl || (photo.file ? URL.createObjectURL(photo.file) : null);
      if (!previewUrl) continue;
      const dataUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.72));
        };
        img.onerror = () => reject(new Error('thumbnail failed'));
        img.src = previewUrl;
      });
      thumbs.push({ name: photo.file?.name || 'Photo', previewUrl: dataUrl });
      if (!photo.previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    } catch {
      // Skip failed thumbnails; confirmation still works without them.
    }
  }
  return thumbs;
}

export function revokePhotoPreviews(photos) {
  (photos || []).forEach((p) => {
    if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
  });
}

export default ComplaintPhotoUpload;
